import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { IoEye } from "react-icons/io5";
import { FiEdit2, FiFilter, FiRotateCcw, FiSearch } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { Apis } from "../../../lib/Apis";
import Loader from "../../../Utils/loader";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";

function ManageAdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nameFilter, setNameFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({});
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 10,
  });

  const buildParams = useCallback(() => {
    const params = {};
    if (nameFilter.trim()) params.name = nameFilter.trim();
    if (emailFilter.trim()) params.email = emailFilter.trim();
    if (statusFilter !== "") params.status = statusFilter;
    return params;
  }, [nameFilter, emailFilter, statusFilter]);

  const fetchUsers = useCallback(async (nextOffset = offset, overrideParams = appliedFilters) => {
    try {
      setIsLoading(true);
      const response = await Apis.getAdminUsers(nextOffset, limit, overrideParams);
      const payload = response.data.data;
      setUsers(payload.users || []);
      setPagination(payload.pagination || {
        totalRecords: 0,
        totalPages: 0,
        currentPage: 1,
        perPage: limit,
      });
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to fetch admin users.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters, offset, limit]);

  useEffect(() => {
    fetchUsers(offset);
  }, [offset, fetchUsers]);

  const handleApplyFilter = () => {
    const nextFilters = buildParams();
    setAppliedFilters(nextFilters);
    setOffset(0);
    fetchUsers(0, nextFilters);
  };

  const handleResetFilter = () => {
    setNameFilter("");
    setEmailFilter("");
    setStatusFilter("");
    setAppliedFilters({});
    setOffset(0);
    fetchUsers(0, {});
  };

  const handleToggleStatus = async (user) => {
    try {
      setLoadingUserId(user.id);
      await Apis.toggleAdminUserStatus(user.id);
      setUsers((prevUsers) =>
        prevUsers.map((item) =>
          item.id === user.id ? { ...item, status: !item.status } : item,
        ),
      );
      Swal.fire("Success", "User status updated successfully.", "success");
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to update user status.", "error");
    } finally {
      setLoadingUserId(null);
    }
  };

  const handleDelete = (user) => {
    Swal.fire({
      title: "Delete user?",
      text: "This admin-created user will be soft deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await Apis.deleteAdminUser(user.id);
        Swal.fire("Deleted", "User deleted successfully.", "success");
        fetchUsers(offset);
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || "Failed to delete user.", "error");
      }
    });
  };

  if (isLoading) {
    return <Loader message="Loading admin users..." />;
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h2 className="text-2xl font-bold">Manage Admin Users</h2>
        <button className="btn btn-filter text-white" onClick={() => navigate("/admin-users/create")}>
          Create New User
        </button>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name"
            value={nameFilter}
            onChange={(event) => setNameFilter(event.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="search-wrapper">
          <input
            type="email"
            className="search-input"
            placeholder="Search by email"
            value={emailFilter}
            onChange={(event) => setEmailFilter(event.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select className="status-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="">Select Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleApplyFilter}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button className="btn btn-reset-filter filter-btn" onClick={handleResetFilter}>
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Name</th>
                <th className="table-th">Email</th>
                <th className="table-th">Roles</th>
                <th className="table-th">Status</th>
                <th className="table-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8">
                    No admin users found.
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user.id} className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"} hover:bg-gray-200 transition-colors duration-300`}>
                    <td className="table-td">{offset + index + 1}</td>
                    <td className="table-td font-medium">{user.name || "-"}</td>
                    <td className="table-td">{user.email}</td>
                    <td className="table-td">{(user.roles || []).map((role) => role.name).join(", ") || "-"}</td>
                    <td className="table-td">
                      <div
                        className={`status-toggle ${user.status ? "active" : ""} ${loadingUserId === user.id ? "loading" : ""}`}
                        onClick={() => loadingUserId !== user.id && handleToggleStatus(user)}
                      >
                        <div className="status-toggle-handle"></div>
                      </div>
                    </td>
                    <td className="table-td">
                      <div className="flex flex-wrap gap-2">
                        <button className="btn btn-view text-white px-3" onClick={() => navigate(`/admin-users/view/${user.id}`, { state: { user, mode: "view" } })}>
                          <IoEye />
                        </button>
                        <button className="btn btn-edit text-white px-3" onClick={() => navigate(`/admin-users/edit/${user.id}`, { state: { user, mode: "edit" } })}>
                          <FiEdit2 />
                        </button>
                        <button className="btn btn-delete text-white px-3" onClick={() => handleDelete(user)}>
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.totalPages > 1 && (
        <div className="mt-5">
          <GlobalPagination offset={offset} limit={limit} total={pagination.totalRecords} onPageChange={setOffset} />
        </div>
      )}
    </div>
  );
}

export default ManageAdminUsers;
